import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Search, Plus, Edit2, Phone, Mail, MessageCircle, UserCheck,
  Filter, CalendarDays, Loader2, BookOpen, Printer, FileSpreadsheet,
  ArrowUp, ArrowDown, ArrowUpDown, History, CheckCircle2, RotateCcw,
  MessageSquare, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/api";
import { AREA_OPTIONS, getAreaLabel } from "@/lib/areaOptions";
import { usePagination } from "@/lib/usePagination";
import {
  normalizeManagementType,
  filterOperatorsByManagementType,
} from "@/lib/notificationRouting";
import { OperatorMultiSelect } from "@/components/ui/operator-multi-select";
import ClientCombobox from "@/components/ClientCombobox";
import SafeModeDiarioGiornaliero from "./SafeModeDiarioGiornaliero";
import DiarioGiornalieroTabRebuilt from "./DiarioGiornalieroTabRebuilt";

const CONTACT_TYPES = [
  { value: "Telefonata", label: "Telefonata", icon: Phone, color: "bg-blue-100 text-blue-700" },
  { value: "Email", label: "Email", icon: Mail, color: "bg-amber-100 text-amber-700" },
  { value: "WhatsApp", label: "WhatsApp", icon: MessageCircle, color: "bg-emerald-100 text-emerald-700" },
  { value: "Presenza", label: "Presenza", icon: UserCheck, color: "bg-violet-100 text-violet-700" },
];

const getContactBadge = (type) => CONTACT_TYPES.find(c => c.value === type) || CONTACT_TYPES[0];

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try { return new Date(dateStr).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return dateStr; }
};

const CLOSED_STATUSES = new Set(["Chiusa", "Conclusa"]);

// Mesi italiani — usati dai filtri Anno/Mese (refinement 22/02/2026).
const MONTHS_IT = [
  { value: "01", label: "Gennaio" },
  { value: "02", label: "Febbraio" },
  { value: "03", label: "Marzo" },
  { value: "04", label: "Aprile" },
  { value: "05", label: "Maggio" },
  { value: "06", label: "Giugno" },
  { value: "07", label: "Luglio" },
  { value: "08", label: "Agosto" },
  { value: "09", label: "Settembre" },
  { value: "10", label: "Ottobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Dicembre" },
];
const monthLabel = (m) => MONTHS_IT.find((x) => x.value === m)?.label || m;
const lastDayOfMonth = (year, monthNum) => {
  const d = new Date(Number(year), Number(monthNum), 0).getDate();
  return String(d).padStart(2, "0");
};

// Italian-friendly action labels for the diary audit trail. Mirrors the
// `_diff_contact_for_audit` mapping on the backend.
const HISTORY_ACTION_LABELS = {
  created: "Creata",
  edited: "Modificata",
  changed_recipient: "Cambiato destinatario",
  changed_practice: "Cambiata pratica",
  changed_client: "Cambiato cliente",
  added_response: "Risposta aggiunta",
  marked_done: "Completata",
  diary_completed: "Completata",
  reply: "Risposta",
};

const formatHistoryWhen = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
    const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    return `${date} alle ${time}`;
  } catch { return iso; }
};

const buildHistorySentence = (entry) => {
  const who = entry?.operatorName || "Operatore";
  const when = formatHistoryWhen(entry?.timestamp);
  const action = HISTORY_ACTION_LABELS[entry?.action] || "Modificata";
  if (entry?.action === "created") {
    return `Creata da ${who} il ${when}`;
  }
  if (entry?.action === "changed_recipient") {
    const prev = entry?.previousValue || "—";
    const next = entry?.newValue || "—";
    return `${action} da ${who} il ${when} (da "${prev}" a "${next}")`;
  }
  if (entry?.action === "edited" && entry?.field) {
    return `${action} ${entry.field} da ${who} il ${when}`;
  }
  return `${action} da ${who} il ${when}`;
};

// Searchable client combobox — extracted to /components/ClientCombobox.jsx
// for reuse across Diario, Pratiche Quotidiane and any future operational
// module that needs to pick a client from the centralized registry.

// Routing per Diario Giornaliero (22/02/2026):
// Default (no `?stage`) = versione RICOSTRUITA stabile (Rebuilt)
// con tutte le funzionalità di scrittura ma SENZA Radix Select /
// Dialog portal-based che causavano i crash.
//   `?stage=legacy` = vecchia versione (può crashare, solo emergenza)
//   `?stage=readonly` o `?stage=safe` = SafeMode read-only puro
//   `?stage=full` o `?stage=rebuilt` = Rebuilt (alias del default)
function readDiaryStageFromURL() {
  if (typeof window === "undefined") return "default";
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get("stage");
    if (raw === "legacy") return "legacy";
    if (raw === "readonly" || raw === "safe") return "readonly";
    return "default";
  } catch { return "default"; }
}

export default function DiarioGiornalieroTab(props) {
  const stage = readDiaryStageFromURL();
  if (stage === "legacy") {
    // Versione vecchia (potenzialmente broken). Solo per emergenza/debug.
    return <DiarioGiornalieroTabFull {...props} />;
  }
  if (stage === "readonly") {
    return <DiarioGiornalieroTabSafeDefault />;
  }
  // Default: versione RICOSTRUITA stabile + full features.
  return <DiarioGiornalieroTabRebuilt {...props} />;
}

function DiarioGiornalieroTabSafeDefault() {
  const goFull = () => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("stage", "full");
    window.location.href = url.toString();
  };
  return (
    <div data-testid="diario-giornaliero-tab">
      <div style={{ background: "#e0f2fe", border: "1px solid #38bdf8", padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
        <strong>Versione stabile in sola lettura.</strong> Creazione/modifica/conclusione note sono temporaneamente disponibili solo nella versione completa.
        <button
          type="button"
          onClick={goFull}
          style={{ marginLeft: 12, padding: "4px 10px", background: "#0284c7", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12 }}
          data-testid="diary-go-full-btn"
        >
          Apri versione completa (?stage=full)
        </button>
      </div>
      <SafeModeDiarioGiornaliero />
    </div>
  );
}

function DiarioGiornalieroTabFull({
  clients = [],
  cases = [],
  operators = [],
  currentOperator,
  operatorRole = "amministratore",
  // Optional plumbing to open the global ClientDialog and create a new client
  // directly from the diary flow (mirrors what ScadenzeTab does)
  onOpenNewClient,
  isClientDialogOpen,
  setIsClientDialogOpen,
  clientForm,
  setClientForm,
  resetClientForm,
  // 11/06/2026 — Quick-action flag from App.js. The top action bar
  // "Diario Giornaliero" button sets pendingQuickAction === "diario:new";
  // this tab consumes it (opens the dialog + clears the flag) after
  // mount, in the same effect that initialises local state.
  pendingQuickAction = null,
  consumeQuickAction = () => {},
}) {
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [clientFilter, setClientFilter] = useState("all");
  const [operatorFilter, setOperatorFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // Filtri Anno / Mese (refinement 22/02/2026) — utili per la
  // revisione del diario prima della fatturazione periodica.
  // Default: anno corrente, mesi "Tutti i mesi".
  const [filterYear, setFilterYear] = useState(() => String(new Date().getFullYear()));
  const [filterMonthFrom, setFilterMonthFrom] = useState("all");
  const [filterMonthTo, setFilterMonthTo] = useState("all");

  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingHistory, setEditingHistory] = useState([]);
  const [editingMeta, setEditingMeta] = useState(null); // {operatorName, createdAt} of the original author
  // S1 Connected-Modules — reply thread state.
  // `replyDraft` is the textarea content. `replySending` blocks double-
  // submit. Both reset on dialog open/close.
  const [replyDraft, setReplyDraft] = useState("");
  const [replySending, setReplySending] = useState(false);
  // S2 Connected-Modules — Deadline-creation flow attached to the note.
  // `linkedDeadlines` is the list of `custom_deadlines` whose
  // sourceModule=="diario" + sourceId==editingId (refreshed at open).
  // `showDeadlineDialog` toggles the inline form.
  const [linkedDeadlines, setLinkedDeadlines] = useState([]);
  const [showDeadlineDialog, setShowDeadlineDialog] = useState(false);
  const [deadlineForm, setDeadlineForm] = useState({
    deadlineDate: "",
    deadlineType: "",
    reminderType: "once",
    notes: "",
  });
  const [deadlineSaving, setDeadlineSaving] = useState(false);
  // S3 Connected-Modules — Practice-creation flow attached to the note.
  // `linkedPractices` is the list of daily_practices whose
  // sourceModule="diario" + sourceContactId==editingId.
  const [linkedPractices, setLinkedPractices] = useState([]);
  const [showPracticeDialog, setShowPracticeDialog] = useState(false);
  const [practiceForm, setPracticeForm] = useState({
    operationalArea: "Lavoro",
    expectedDueDate: "",
    description: "",
  });
  const [practiceSaving, setPracticeSaving] = useState(false);
  const [form, setForm] = useState({});

  // Completion dialog state (mirror Scadenze closure UX). Asks for a
  // completion date (default today) before posting to backend.
  const [completingContact, setCompletingContact] = useState(null);
  const [completionDate, setCompletionDate] = useState("");

  // Paginazione globale condivisa (spec utente 20/02/2026):
  // default 50 righe + selettore 10/30/50/100. La preferenza è
  // persistita per "diario-attive" / "diario-completate" così
  // l'operatore ritrova la propria scelta. Hook applicato più sotto,
  // dopo che `activeContacts` / `completedContacts` sono stati
  // calcolati. Sostituisce la vecchia CTA "Mostra altre 200" che
  // serviva a evitare paint da 1500+ righe (problema ora coperto
  // dal cap a 100 max per pagina del nuovo sistema).

  // Sorting (3-step toggle: null → asc → desc → null)
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const handleSort = (field) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDir("asc");
      return;
    }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    if (sortDir === "desc") { setSortField(null); setSortDir(null); return; }
    setSortDir("asc");
  };
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="h-3 w-3 text-blue-600" /> : <ArrowDown className="h-3 w-3 text-blue-600" />;
  };

  // Active (non-closed) practices only
  const activeCases = useMemo(() =>
    cases.filter(c => !CLOSED_STATUSES.has(c.status)),
  [cases]);

  // Cases for selected client
  const clientActiveCases = useMemo(() => {
    if (!form.clientId) return [];
    return activeCases.filter(c => c.clientId === form.clientId);
  }, [form.clientId, activeCases]);

  const loadContacts = useCallback(async () => {
    setIsLoading(true);
    try { setContacts(await api.getContacts()); }
    catch (e) { console.error("Load error:", e); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  // Unique clients/operators from contacts for filters
  const contactClients = useMemo(() => {
    const map = new Map();
    contacts.forEach(c => { if (c.clientId) map.set(c.clientId, c.clientName); });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [contacts]);

  const dateBounds = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    const sevenDaysAgo = new Date(today); sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const fifteenDaysAgo = new Date(today); fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
    const oneMonthAgo = new Date(today); oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const tomorrow = new Date(today.getTime() + 86400000);
    return { today, yesterday, sevenDaysAgo, fifteenDaysAgo, oneMonthAgo, tomorrow };
  }, []);

  // Build case-to-management map for filtering
  const caseManagementMap = useMemo(() => {
    const map = {};
    cases.forEach(c => { map[c.id] = c.practiceType; });
    return map;
  }, [cases]);

  const filteredContacts = useMemo(() => {
    let list = [...contacts];
    
    // Date filter
    if (dateFilter === "custom") {
      if (dateFrom) list = list.filter(c => c.contactDate >= dateFrom);
      if (dateTo) list = list.filter(c => c.contactDate <= dateTo);
    } else if (dateFilter !== "all") {
      list = list.filter(c => {
        const d = new Date(c.contactDate);
        if (dateFilter === "oggi") return d >= dateBounds.today && d < dateBounds.tomorrow;
        if (dateFilter === "ieri") return d >= dateBounds.yesterday && d < dateBounds.today;
        if (dateFilter === "7giorni") return d >= dateBounds.sevenDaysAgo;
        if (dateFilter === "15giorni") return d >= dateBounds.fifteenDaysAgo;
        if (dateFilter === "mese") return d >= dateBounds.oneMonthAgo;
        return true;
      });
    }

    // Filtro Anno / Mese (refinement 22/02/2026). Si applica sopra al
    // filtro Periodo (intersezione): la riga deve passare ENTRAMBI per
    // essere visibile. Quando Anno = "all" il filtro è disattivato.
    if (filterYear && filterYear !== "all") {
      const mFrom = filterMonthFrom !== "all" ? filterMonthFrom : null;
      const mToRaw = filterMonthTo !== "all" ? filterMonthTo : null;
      let m1 = mFrom;
      let m2 = mToRaw || mFrom;
      if (m1 && m2 && m1 > m2) { const t = m1; m1 = m2; m2 = t; }
      const ymFrom = m1 ? `${filterYear}-${m1}-01` : `${filterYear}-01-01`;
      const ymTo = m2 ? `${filterYear}-${m2}-${lastDayOfMonth(filterYear, m2)}` : `${filterYear}-12-31`;
      list = list.filter(c => {
        const d = (c.contactDate || "").slice(0, 10);
        return d && d >= ymFrom && d <= ymTo;
      });
    }
    
    // Management filter (via linked case)
    if (clientFilter !== "all") list = list.filter(c => c.clientId === clientFilter);
    // Filter "Operatore" in this view means DESTINATARIO of the diary
    // entry (i.e. "whom is this for"). The author's operatorId is
    // intentionally NOT considered — the office wants to surface the
    // workload of the receiving operator, not who wrote the note.
    if (operatorFilter !== "all") list = list.filter(c => c.destinatarioId === operatorFilter);
    if (typeFilter !== "all") list = list.filter(c => c.contactType === typeFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(c =>
        (c.clientName || "").toLowerCase().includes(q) ||
        (c.caseNumber || "").toLowerCase().includes(q) ||
        (c.content || "").toLowerCase().includes(q) ||
        (c.operatorName || "").toLowerCase().includes(q) ||
        (c.destinatarioName || "").toLowerCase().includes(q)
      );
    }

    // Apply active sort (sortField + sortDir 'asc'|'desc'|null)
    if (sortField && sortDir) {
      const getter = (c) => {
        switch (sortField) {
          case "data": return `${c.contactDate || ""} ${c.contactTime || ""}`;
          case "ora": return c.contactTime || "";
          case "tipo": return c.contactType || "";
          case "cliente": return (c.clientName || "").toLowerCase();
          case "pratica": return c.caseNumber || "";
          case "operatore": return (c.operatorName || "").toLowerCase();
          case "destinatario": return (c.destinatarioName || "").toLowerCase();
          default: return "";
        }
      };
      list = [...list].sort((a, b) => {
        const va = getter(a), vb = getter(b);
        if (va < vb) return sortDir === "asc" ? -1 : 1;
        if (va > vb) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [contacts, dateFilter, dateFrom, dateTo, clientFilter, operatorFilter, typeFilter, searchQuery, dateBounds, sortField, sortDir, filterYear, filterMonthFrom, filterMonthTo]);

  // Lista unificata (refinement UI 22/02/2026): tutte le note (attive +
  // completate) appaiono in un'unica tabella ordinata. Lo stato
  // "Concluso" resta come flag sulla singola riga per la futura
  // revisione amministrativa / fatturazione periodica.
  const activeContacts = useMemo(
    () => filteredContacts.filter(c => !c.completedAt),
    [filteredContacts]
  );
  const completedContacts = useMemo(
    () => filteredContacts.filter(c => !!c.completedAt),
    [filteredContacts]
  );

  // Paginazione globale unificata (refinement UI 22/02/2026).
  const {
    paged: pagedContacts,
    paginationBar: contactsPaginationBar,
  } = usePagination(filteredContacts, {
    storageKey: "diario-tutte",
    itemLabelSingular: "nota",
    itemLabelPlural: "note",
  });

  const handleCompleteOpen = (contact) => {
    setCompletingContact(contact);
    setCompletionDate(new Date().toISOString().slice(0, 10));
  };
  const handleCompleteConfirm = async () => {
    if (!completingContact) return;
    const id = completingContact.id;
    try {
      const saved = await api.completeContact(id, completionDate || "");
      if (saved?.id) {
        setContacts(prev => prev.map(c => (c.id === saved.id ? saved : c)));
      }
      setCompletingContact(null);
      toast.success("Nota completata");
    } catch (e) {
      toast.error(e?.message || "Errore durante il completamento");
    }
  };
  const handleReopen = async (contact) => {
    try {
      const saved = await api.reopenContact(contact.id);
      if (saved?.id) {
        setContacts(prev => prev.map(c => (c.id === saved.id ? saved : c)));
      }
      toast.success("Nota riaperta");
    } catch (e) {
      toast.error(e?.message || "Errore durante la riapertura");
    }
  };

  const emptyForm = () => ({
    clientId: "",
    clientName: "",
    caseId: "",
    caseNumber: "",
    contactType: "Telefonata",
    content: "",
    contactDate: new Date().toISOString().slice(0, 10),
    contactTime: new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }),
    operatorId: currentOperator?.id || "",
    operatorName: currentOperator?.fullName || currentOperator?.name || "",
    destinatarioId: "",
    destinatarioName: "",
    destinatarioIds: [],
    managementType: "agenzia", // override-able via "Pratica collegata" select
    // Operational simplification (18/02/2026). By default a diary
    // entry is INFORMATIONAL (= auto-archived on save). The operator
    // can flag it as "Richiede azione" to keep it in the active list
    // and fire the assignment notification.
    requiresAction: false,
  });

  const openNew = () => { setForm(emptyForm()); setEditingId(null); setEditingHistory([]); setEditingMeta(null); setReplyDraft(""); setShowDialog(true); };

  // Quick-action shortcut: il bottone "Diario Giornaliero" in App.js
  // dispatcha `diario:open-new-note` dopo aver attivato il sub-tab.
  // L'effect intercetta l'evento e apre direttamente il dialog "Nuova
  // Nota", trasformando il quick-button in un flow di produttività
  // immediato.
  useEffect(() => {
    const onOpenNew = () => openNew();
    window.addEventListener("diario:open-new-note", onOpenNew);
    return () => window.removeEventListener("diario:open-new-note", onOpenNew);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOperator]);

  // 11/06/2026 — Prop-based quick-action. Consumes the
  // `pendingQuickAction` flag set by App.js when the top action bar
  // "Diario Giornaliero" button is clicked. This is more reliable than
  // the legacy window-event approach above (kept for backward compat
  // with any other dispatcher), because it survives the conditional
  // mount of the sub-tab: the effect runs ONCE the listener-side
  // component is actually rendered, no timing race.
  useEffect(() => {
    if (pendingQuickAction === "diario:new") {
      openNew();
      consumeQuickAction("diario:new");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingQuickAction]);

  const openEdit = (contact) => {
    setForm({
      clientId: contact.clientId || "",
      clientName: contact.clientName || "",
      caseId: contact.caseId || "",
      caseNumber: contact.caseNumber || "",
      contactType: contact.contactType || "Telefonata",
      content: contact.content || "",
      contactDate: contact.contactDate ? contact.contactDate.slice(0, 10) : "",
      contactTime: contact.contactTime || "",
      operatorId: contact.operatorId || "",
      operatorName: contact.operatorName || "",
      destinatarioId: contact.destinatarioId || "",
      destinatarioName: contact.destinatarioName || "",
      destinatarioIds: contact.destinatarioIds || (contact.destinatarioId ? [contact.destinatarioId] : []),
      // Falls back to the linked case's practiceType when the field was
      // not persisted (legacy contacts). Default "agenzia" mirrors the
      // backend `_derive_management_type_from_case` default.
      managementType: normalizeManagementType(
        contact.managementType
          || (contact.caseId ? caseManagementMap[contact.caseId] : "")
      ),
      // Edit form: respect whatever the note was created with. Legacy
      // entries lacking the field are treated as actionable (so the
      // toggle is ON) — this preserves their visible behaviour.
      requiresAction: contact.requiresAction === undefined
        ? !contact.completedAt
        : !!contact.requiresAction,
    });
    setEditingId(contact.id);
    setEditingHistory(Array.isArray(contact.history) ? contact.history : []);
    setEditingMeta({
      operatorName: contact.operatorName || "",
      createdAt: contact.createdAt || "",
    });
    setReplyDraft("");
    // S2 — fetch linked deadlines (best-effort, non-blocking).
    setLinkedDeadlines([]);
    if (contact.id) {
      api.getCustomDeadlinesBySource("diario", contact.id)
        .then((arr) => setLinkedDeadlines(Array.isArray(arr) ? arr : []))
        .catch(() => setLinkedDeadlines([]));
      // S3 — fetch linked practices (best-effort, non-blocking).
      api.getDailyPracticesBySource("diario", contact.id)
        .then((arr) => setLinkedPractices(Array.isArray(arr) ? arr : []))
        .catch(() => setLinkedPractices([]));
    } else {
      setLinkedPractices([]);
    }
    setShowDeadlineDialog(false);
    setShowPracticeDialog(false);
    setShowDialog(true);
  };

  // Helper: oggi + N giorni in formato YYYY-MM-DD.
  const _addDaysIso = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };

  // S2 — apre il dialog "Crea Scadenza" precompilando i campi a
  // partire dalla nota corrente. Il `practiceType` viene mappato dal
  // `managementType` della nota (studio/agenzia). `areaType` è
  // popolato solo per le note Studio (vincolo backend Phase-1).
  const openDeadlineDialog = () => {
    if (!editingId) return;
    setDeadlineForm({
      deadlineDate: _addDaysIso(7),  // default 7 giorni nel futuro
      deadlineType: "",
      reminderType: "once",
      notes: (form.content || "").slice(0, 280),
    });
    setShowDeadlineDialog(true);
  };

  const handleCreateDeadline = async () => {
    if (!editingId || deadlineSaving) return;
    if (!deadlineForm.deadlineDate || !deadlineForm.deadlineType.trim()) {
      toast.error("Data e tipo scadenza sono obbligatori");
      return;
    }
    setDeadlineSaving(true);
    try {
      const practiceType = (form.managementType || "agenzia") === "studio" ? "studio" : "agenzia";
      const payload = {
        clientId: form.clientId || "",
        clientName: form.clientName || "",
        practiceType,
        deadlineType: deadlineForm.deadlineType.trim(),
        deadlineDate: deadlineForm.deadlineDate,
        notes: deadlineForm.notes || "",
        reminderType: deadlineForm.reminderType || "once",
        // areaType valido solo per Studio (vincolo backend Phase-1).
        ...(practiceType === "studio" ? { areaType: "Lavoro" } : {}),
        // Cross-module linkage.
        sourceModule: "diario",
        sourceId: editingId,
      };
      const created = await api.createCustomDeadline(payload);
      setLinkedDeadlines((prev) => [...prev, created]);
      // Refresh contact history (server appended `deadline_created`).
      try {
        const refreshed = await api.getContacts();
        const updated = (refreshed || []).find((c) => c.id === editingId);
        if (updated && Array.isArray(updated.history)) {
          setEditingHistory(updated.history);
          setContacts(prev => prev.map(c => c.id === editingId ? { ...c, ...updated, id: editingId } : c));
        }
      } catch (_) { /* best-effort */ }
      setShowDeadlineDialog(false);
      toast.success("Scadenza creata");
    } catch (e) {
      toast.error(`Errore creazione scadenza: ${e.message || e}`);
    } finally {
      setDeadlineSaving(false);
    }
  };

  // S3 — apre il dialog "Nuova Pratica" precompilando i campi dalla
  // nota. La nota NON viene chiusa (decisione utente C nel proposal):
  // l'operatore potrebbe voler aggiungere info post-conversione.
  const openPracticeDialog = () => {
    if (!editingId) return;
    const todayIso = new Date().toISOString().slice(0, 10);
    setPracticeForm({
      operationalArea: "Lavoro",
      expectedDueDate: todayIso,
      description: form.content || "",
    });
    setShowPracticeDialog(true);
  };

  const handleCreatePractice = async () => {
    if (!editingId || practiceSaving) return;
    if (!practiceForm.description.trim()) {
      toast.error("La descrizione è obbligatoria");
      return;
    }
    setPracticeSaving(true);
    try {
      const managementType = (form.managementType || "agenzia") === "studio" ? "studio" : "agenzia";
      const today = new Date().toISOString().slice(0, 10);
      const payload = {
        clientId: form.clientId || "",
        clientName: form.clientName || "",
        managementType,
        operationalArea: practiceForm.operationalArea || "Lavoro",
        expectedDueDate: practiceForm.expectedDueDate || today,
        description: practiceForm.description.trim(),
        expenses: { amount: 0, paid: false, description: "" },
        status: "aperta",
        // Cross-module linkage.
        sourceModule: "diario",
        sourceContactId: editingId,
      };
      const created = await api.createDailyPractice(payload);
      setLinkedPractices((prev) => [...prev, created]);
      // Refresh contact history (server appended `converted_to_practice`).
      try {
        const refreshed = await api.getContacts();
        const updated = (refreshed || []).find((c) => c.id === editingId);
        if (updated && Array.isArray(updated.history)) {
          setEditingHistory(updated.history);
          setContacts(prev => prev.map(c => c.id === editingId ? { ...c, ...updated, id: editingId } : c));
        }
      } catch (_) { /* best-effort */ }
      setShowPracticeDialog(false);
      toast.success("Pratica creata");
    } catch (e) {
      toast.error(`Errore creazione pratica: ${e.message || e}`);
    } finally {
      setPracticeSaving(false);
    }
  };

  // S1 Connected-Modules — send an internal reply to the diary note.
  // Optimistic update: appended locally to `editingHistory` so the
  // thread surfaces immediately, then reconciled with the server
  // response (which is authoritative for the timestamp + canonical
  // operator name).
  const handleSendReply = async () => {
    const content = replyDraft.trim();
    if (!content || !editingId || replySending) return;
    setReplySending(true);
    try {
      const refreshed = await api.replyToContact(editingId, content);
      setEditingHistory(Array.isArray(refreshed.history) ? refreshed.history : editingHistory);
      // Patch the row in the visible list so the (operatore-visible) badge
      // counter stays in sync without a full reload.
      setContacts(prev => prev.map(c => c.id === editingId ? { ...c, ...refreshed, id: editingId } : c));
      setReplyDraft("");
      toast.success("Risposta inviata");
    } catch (e) {
      toast.error(`Errore invio risposta: ${e.message || e}`);
    } finally {
      setReplySending(false);
    }
  };

  const handleSave = async () => {
    if (!form.clientId || !form.content.trim()) return;
    setIsSaving(true);
    try {
      const payload = { ...form };
      const isUpdate = !!editingId;
      // Hot path: server returns the created/updated ContactResponse. We
      // patch the local list in place instead of re-fetching all 1559+
      // records (perf regression, full reload was ~1.2 MB over the wire
      // PLUS the parse/reconcile cost on every save → felt slow).
      const saved = isUpdate
        ? await api.updateContact(editingId, payload)
        : await api.createContact(payload);
      // React 19 + Radix Dialog hardening: close the dialog FIRST and clear
      // `isSaving` synchronously so no state update happens inside the
      // closing portal during its ~200ms exit animation. We then patch the
      // list right after the close animation (250ms is the Radix Dialog
      // exit duration — outlasting it prevents the removeChild race).
      setIsSaving(false);
      setShowDialog(false);
      const successMsg = isUpdate ? "Nota aggiornata" : "Nota creata";
      setTimeout(() => {
        if (saved && saved.id) {
          if (isUpdate) {
            setContacts((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
          } else {
            setContacts((prev) => [saved, ...prev]);
          }
        } else {
          // Fallback only if the backend didn't echo back the document.
          loadContacts();
        }
        toast.success(successMsg);
      }, 250);
    } catch (e) {
      console.error("Save error:", e);
      toast.error(e?.message || "Errore durante il salvataggio");
      setIsSaving(false);
    }
  };

  // Build a flat array of rows representing the currently visible diary list
  // (already filtered + sorted) for export/print
  const buildExportRows = () => filteredContacts.map(c => ({
    Data: formatDate(c.contactDate),
    Ora: c.contactTime || "",
    Tipo: c.contactType || "",
    Cliente: c.clientName || "",
    Pratica: c.caseNumber || "",
    Descrizione: (c.content || "").replace(/[\r\n]+/g, " ").trim(),
    Operatore: c.operatorName || "",
    Destinatario: c.destinatarioName || "",
    Stato: c.completedAt ? "Chiusa" : "In corso",
  }));

  // Riepilogo testuale dei filtri attivi: incluso negli header di
  // stampa/export così l'operatore sa esattamente cosa contiene il
  // documento — utile per la revisione pre-fatturazione periodica.
  const buildFilterSummary = () => {
    const parts = [];
    if (searchQuery.trim()) parts.push(`Cerca: "${searchQuery.trim()}"`);
    if (dateFilter !== "all") {
      const labels = { oggi: "Oggi", ieri: "Ieri", "7giorni": "Ultimi 7gg", "15giorni": "Ultimi 15gg", mese: "Ultimo mese", custom: "Personalizzato" };
      let s = `Periodo: ${labels[dateFilter] || dateFilter}`;
      if (dateFilter === "custom" && (dateFrom || dateTo)) s += ` (${dateFrom || "…"} → ${dateTo || "…"})`;
      parts.push(s);
    }
    if (filterYear && filterYear !== "all") {
      let s = `Anno: ${filterYear}`;
      const mFrom = filterMonthFrom !== "all" ? filterMonthFrom : null;
      const mTo = filterMonthTo !== "all" ? filterMonthTo : null;
      if (mFrom && mTo && mFrom !== mTo) s += ` · Mesi: ${monthLabel(mFrom)} → ${monthLabel(mTo)}`;
      else if (mFrom || mTo) s += ` · Mese: ${monthLabel(mFrom || mTo)}`;
      parts.push(s);
    }
    if (typeFilter !== "all") parts.push(`Tipo: ${typeFilter}`);
    if (clientFilter !== "all") {
      const cl = clients.find(c => c.id === clientFilter);
      if (cl) parts.push(`Cliente: ${cl.fullName || cl.name}`);
    }
    if (operatorFilter !== "all") {
      const op = operators.find(o => o.id === operatorFilter);
      if (op) parts.push(`Destinatario: ${op.fullName || op.name}`);
    }
    return parts.join(" · ");
  };

  // Export to Excel as CSV with UTF-8 BOM (opens cleanly in Excel/LibreOffice
  // including Italian accents, no extra deps required)
  const handleExportExcel = () => {
    try {
      const rows = buildExportRows();
      if (rows.length === 0) {
        toast.info("Nessuna nota da esportare");
        return;
      }
      const headers = Object.keys(rows[0]);
      const escape = (v) => {
        const s = String(v ?? "");
        return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = [
        headers.join(";"),
        ...rows.map(r => headers.map(h => escape(r[h])).join(";")),
      ].join("\r\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `diario_giornaliero_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Esportate ${rows.length} note`);
    } catch (e) {
      toast.error("Errore durante l'esportazione");
    }
  };

  // Open a printable report in a new window with the filtered+sorted list
  const handlePrint = () => {
    const rows = buildExportRows();
    const w = window.open("", "_blank", "width=1100,height=800");
    if (!w) {
      toast.error("Apertura finestra di stampa bloccata dal browser");
      return;
    }
    const todayStr = new Date().toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const filtersSummary = buildFilterSummary();
    const headers = ["Data", "Ora", "Tipo", "Cliente", "Pratica", "Descrizione", "Operatore", "Destinatario", "Stato"];
    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"/>
      <title>Diario Giornaliero</title>
      <style>
        *{box-sizing:border-box} body{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#1e293b;padding:24px;font-size:11px}
        h1{margin:0 0 4px;font-size:18px} .meta{color:#64748b;font-size:11px;margin-bottom:16px}
        .filters{color:#475569;font-size:10.5px;margin:-8px 0 14px;font-style:italic}
        table{width:100%;border-collapse:collapse;font-size:10.5px}
        th{text-align:left;background:#f1f5f9;color:#0f172a;font-weight:600;border-bottom:2px solid #cbd5e1;padding:6px 8px}
        td{padding:5px 8px;border-bottom:1px solid #e2e8f0;vertical-align:top}
        tr:nth-child(even) td{background:#fafafa}
        .desc{max-width:380px;word-wrap:break-word}
        @media print{ body{padding:8mm} button{display:none} }
      </style></head><body>
      <h1>Diario Giornaliero</h1>
      <div class="meta">Stampato il ${todayStr} · ${rows.length} note</div>
      ${filtersSummary ? `<div class="filters">Filtri: ${filtersSummary.replace(/[<>&]/g, ch => ({ "<":"&lt;",">":"&gt;","&":"&amp;" }[ch]))}</div>` : ""}
      <table>
        <thead><tr>${headers.map(h => `<th>${h}</th>`).join("")}</tr></thead>
        <tbody>
          ${rows.map(r => `<tr>
            <td>${r.Data || ""}</td>
            <td>${r.Ora || ""}</td>
            <td>${r.Tipo || ""}</td>
            <td>${r.Cliente || ""}</td>
            <td>${r.Pratica || ""}</td>
            <td class="desc">${(r.Descrizione || "").replace(/[<>&]/g, ch => ({ "<":"&lt;",">":"&gt;","&":"&amp;" }[ch]))}</td>
            <td>${r.Operatore || ""}</td>
            <td>${r.Destinatario || ""}</td>
            <td>${r.Stato || ""}</td>
          </tr>`).join("")}
        </tbody>
      </table>
      <script>window.onload=()=>{setTimeout(()=>window.print(),200);}</script>
      </body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  /**
   * Stampa di una SINGOLA nota del Diario Giornaliero (20/02/2026).
   *
   * Apre una scheda one-page A4 con tutti i campi della nota
   * (data, ora, tipo, cliente, pratica linkata, operatore,
   * destinatario, contenuto integrale, eventuale chiusura).
   * Comoda per archivio cartaceo o per condividere il dettaglio
   * con il cliente. Riusa lo stesso pattern HTML+CSS del Registro.
   */
  const handlePrintSingleNote = (c) => {
    if (!c) return;
    const w = window.open("", "_blank", "width=900,height=900");
    if (!w) {
      toast.error("Apertura finestra di stampa bloccata dal browser");
      return;
    }
    const esc = (s) => String(s ?? "").replace(/[<>&]/g, (ch) => ({ "<":"&lt;",">":"&gt;","&":"&amp;" }[ch]));
    const todayStr = new Date().toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const contactDate = c.contactDate ? formatDate(c.contactDate) : "—";
    const isCompleted = !!c.completedAt;
    const completedAt = c.completedAt
      ? new Date(c.completedAt).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })
      : "";
    // Estrai le risposte operative dalla cronologia (history[]) della
    // nota — sono le entry con `action="reply"`. Fix 20/02/2026: prima
    // la stampa scheda mostrava solo "Contenuto" e i campi di
    // intestazione, perdendo l'intero thread di risposte operative.
    const replyEntries = Array.isArray(c.history)
      ? c.history.filter((h) => h?.action === "reply" && (h.details || h.text))
      : [];
    const fmtReplyDate = (t) => {
      if (!t) return "";
      try {
        return new Date(t).toLocaleString("it-IT", {
          day: "2-digit", month: "2-digit", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        });
      } catch { return t; }
    };

    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"/>
      <title>Nota Diario — ${esc(c.clientName || "")}</title>
      <style>
        *{box-sizing:border-box}
        body{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#1e293b;padding:28px;font-size:12px;max-width:780px;margin:0 auto}
        h1{margin:0 0 4px;font-size:20px;color:#0f172a}
        .meta{color:#64748b;font-size:11px;margin-bottom:16px}
        .badge{display:inline-block;padding:2px 8px;border-radius:9999px;font-size:10.5px;font-weight:600;margin-left:6px}
        .b-tipo{background:#e0e7ff;color:#3730a3}
        .b-attiva{background:#fef9c3;color:#854d0e}
        .b-completata{background:#dcfce7;color:#166534}
        table.fields{width:100%;border-collapse:collapse;margin-top:8px}
        table.fields td{padding:7px 10px;border-bottom:1px solid #e2e8f0;vertical-align:top;font-size:12px}
        table.fields td.label{width:35%;color:#475569;font-weight:600;background:#f8fafc}
        .section-title{margin:18px 0 6px;font-size:13px;font-weight:700;color:#0f172a;text-transform:uppercase;letter-spacing:.04em;border-bottom:2px solid #cbd5e1;padding-bottom:4px}
        .longtext{white-space:pre-wrap;background:#fafafa;border:1px solid #e2e8f0;padding:10px 12px;border-radius:6px;font-size:12px;line-height:1.5}
        .reply{margin:8px 0;padding:9px 12px;border-left:3px solid #0ea5e9;background:#f0f9ff;border-radius:0 6px 6px 0}
        .reply .who{font-size:10.5px;color:#0369a1;font-weight:600;margin-bottom:3px}
        .reply .when{font-size:10px;color:#64748b;margin-left:6px;font-weight:400}
        .reply .text{font-size:12px;color:#0f172a;white-space:pre-wrap;line-height:1.5}
        .footer{margin-top:24px;color:#94a3b8;font-size:10px;border-top:1px solid #e2e8f0;padding-top:8px}
        @media print{ body{padding:8mm;max-width:none} button{display:none} }
      </style></head><body>
      <h1>Nota Diario Giornaliero
        ${c.contactType ? `<span class="badge b-tipo">${esc(c.contactType)}</span>` : ""}
        <span class="badge ${isCompleted ? "b-completata" : "b-attiva"}">${isCompleted ? "Completata" : "Attiva"}</span>
      </h1>
      <div class="meta">Stampato il ${todayStr} · ID ${esc(c.id || "—")}</div>

      <div class="section-title">Dati principali</div>
      <table class="fields">
        <tr><td class="label">Data</td><td>${esc(contactDate)}${c.contactTime ? ` · ${esc(c.contactTime)}` : ""}</td></tr>
        <tr><td class="label">Cliente</td><td>${esc(c.clientName || "—")}</td></tr>
        ${c.caseNumber ? `<tr><td class="label">Pratica</td><td>${esc(c.caseNumber)}</td></tr>` : ""}
        <tr><td class="label">Operatore di origine</td><td>${esc(c.operatorName || "—")}</td></tr>
        ${c.destinatarioName ? `<tr><td class="label">Destinatario attuale</td><td>${esc(c.destinatarioName)}</td></tr>` : ""}
        ${c.requiresAction ? `<tr><td class="label">Richiede azione</td><td>Sì</td></tr>` : ""}
      </table>

      <div class="section-title">Descrizione (argomento principale)</div>
      <div class="longtext">${esc(c.content || "(nessun contenuto)")}</div>

      ${replyEntries.length > 0 ? `
        <div class="section-title">Risposte Operative (${replyEntries.length})</div>
        ${replyEntries.map((r) => `
          <div class="reply">
            <div class="who">${esc(r.operatorName || r.userName || r.actor || "Operatore")}
              <span class="when">· ${esc(fmtReplyDate(r.timestamp))}</span>
            </div>
            <div class="text">${esc(r.details || r.text || "")}</div>
          </div>
        `).join("")}
      ` : ""}

      ${isCompleted ? `
        <div class="section-title">Completamento</div>
        <table class="fields">
          <tr><td class="label">Completata il</td><td>${esc(completedAt)}</td></tr>
          ${c.completedByOperatorName ? `<tr><td class="label">Completata da</td><td>${esc(c.completedByOperatorName)}</td></tr>` : ""}
          ${c.completionNotes ? `<tr><td class="label">Note</td><td>${esc(c.completionNotes)}</td></tr>` : ""}
        </table>
      ` : ""}

      <div class="footer">Gestionale Gruppo Impresa · Scheda generata automaticamente</div>
      <script>window.onload=()=>{setTimeout(()=>window.print(),200);}</script>
      </body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const handleClientChange = (clientId, clientName) => {
    setForm(f => ({ ...f, clientId, clientName, caseId: "", caseNumber: "" }));
  };

  // When the user clicks "+ Crea nuovo cliente" in the combobox, we open the
  // global ClientDialog with the typed name pre-filled and remember the diary
  // form so we can auto-select the new client after it is saved.
  const [pendingDiaryForm, setPendingDiaryForm] = useState(null);
  const handleCreateNewClientFromDiary = (typedName) => {
    if (!onOpenNewClient) return;
    setPendingDiaryForm({ ...form, _typedName: typedName });
    setShowDialog(false);
    if (resetClientForm) resetClientForm();
    if (setClientForm) setClientForm(prev => ({ ...(prev || {}), fullName: typedName }));
    onOpenNewClient();
  };

  // After the global ClientDialog closes, if we were in the "create from diary" flow,
  // look up the newly created client and auto-select it, then re-open the diary dialog.
  useEffect(() => {
    if (!pendingDiaryForm) return;
    if (isClientDialogOpen) return; // wait until the dialog is closed
    const typed = (pendingDiaryForm._typedName || "").toLowerCase();
    const found = clients.find(c => (c.fullName || "").toLowerCase() === typed);
    setForm({
      ...pendingDiaryForm,
      clientId: found?.id || pendingDiaryForm.clientId || "",
      clientName: found?.fullName || pendingDiaryForm.clientName || "",
      caseId: "",
      caseNumber: "",
    });
    setPendingDiaryForm(null);
    setShowDialog(true);
  }, [isClientDialogOpen, pendingDiaryForm, clients]);

  const handleCaseChange = (caseId) => {
    if (caseId === "none") {
      // No linked case → preserve operator-picked managementType (or
      // default "agenzia"); just clear the case fields.
      setForm(f => ({ ...f, caseId: "", caseNumber: "" }));
      return;
    }
    const c = cases.find(cs => cs.id === caseId);
    setForm(f => ({
      ...f,
      caseId,
      caseNumber: c?.caseNumber || "",
      // Sync managementType with the linked case's practiceType so the
      // destinatario dropdown filters to the right audience. Operators
      // can still override via the explicit selector if needed.
      managementType: normalizeManagementType(c?.practiceType),
      // Clear an incompatible previously-selected destinatario.
      destinatarioId: "",
      destinatarioName: "",
      destinatarioIds: [],
    }));
  };

  const resetFilters = () => {
    setSearchQuery("");
    setDateFilter("all");
    setDateFrom("");
    setDateTo("");
    setClientFilter("all");
    setOperatorFilter("all");
    setTypeFilter("all");
    setFilterYear(String(new Date().getFullYear()));
    setFilterMonthFrom("all");
    setFilterMonthTo("all");
  };
  const hasActiveFilters = Boolean(
    dateFilter !== "all" ||
    clientFilter !== "all" ||
    operatorFilter !== "all" ||
    typeFilter !== "all" ||
    searchQuery.trim() ||
    filterYear !== "all" ||
    filterMonthFrom !== "all" ||
    filterMonthTo !== "all"
  );

  // Anni disponibili: data-driven dai `contacts` + anno corrente.
  const filterYearOptions = useMemo(() => {
    const set = new Set();
    const cy = new Date().getFullYear();
    set.add(String(cy));
    contacts.forEach((c) => {
      const d = (c.contactDate || "").slice(0, 10);
      if (d && /^\d{4}-/.test(d)) set.add(d.slice(0, 4));
    });
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [contacts]);

  if (isLoading) {
    return (<div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /><span className="ml-3 text-gray-600">Caricamento diario...</span></div>);
  }

  return (
    <div className="space-y-4" data-testid="diario-giornaliero-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-slate-600" />
            Diario Giornaliero
          </h2>
          <p className="text-sm text-gray-500">
            {filteredContacts.length} {filteredContacts.length === 1 ? "nota" : "note"}
            {completedContacts.length > 0 ? ` · ${activeContacts.length} in corso · ${completedContacts.length} ${completedContacts.length === 1 ? "conclusa" : "concluse"}` : ""}
            {hasActiveFilters ? " (filtrate)" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            data-testid="diary-print-btn"
            title={`Stampa ${filteredContacts.length} righe filtrate`}
          >
            <Printer className="h-4 w-4 mr-1.5" /> Stampa
            <span className="ml-1 text-xs opacity-70">({filteredContacts.length})</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportExcel}
            data-testid="diary-export-excel-btn"
            title={`Esporta ${filteredContacts.length} righe filtrate`}
          >
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Esporta Excel
            <span className="ml-1 text-xs opacity-70">({filteredContacts.length})</span>
          </Button>
          <Button onClick={openNew} data-testid="new-entry-btn" className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" /> Nuova Nota
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Ricerca testo libero */}
          <div className="flex-1 min-w-[200px] flex flex-col gap-0.5">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Cerca
            </label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input placeholder="Cliente, pratica, contenuto..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9" data-testid="diary-search" />
            </div>
          </div>
          {/* Periodo */}
          <div className="flex flex-col gap-0.5 min-w-[140px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Periodo
            </label>
            <Select value={dateFilter} onValueChange={v => { setDateFilter(v); if (v !== "custom") { setDateFrom(""); setDateTo(""); } }}>
              <SelectTrigger className="w-[140px] h-9" data-testid="diary-date-filter"><CalendarDays className="h-3.5 w-3.5 mr-1.5 text-gray-400" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="oggi">Oggi</SelectItem>
                <SelectItem value="ieri">Ieri</SelectItem>
                <SelectItem value="7giorni">Ultimi 7gg</SelectItem>
                <SelectItem value="15giorni">Ultimi 15gg</SelectItem>
                <SelectItem value="mese">Ultimo mese</SelectItem>
                <SelectItem value="custom">Personalizzato</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {dateFilter === "custom" && (
            <>
              <div className="flex flex-col gap-0.5">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">Dal</label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[130px] h-9 text-sm" data-testid="diary-date-from" />
              </div>
              <div className="flex flex-col gap-0.5">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">Al</label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[130px] h-9 text-sm" data-testid="diary-date-to" />
              </div>
            </>
          )}
          {/* Anno (refinement 22/02/2026) — default anno corrente.
               `startTransition` evita race React 19 portal/reconciliation. */}
          <div className="flex flex-col gap-0.5 min-w-[110px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Anno
            </label>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-[110px] h-9" data-testid="diary-year-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {filterYearOptions.map(y => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Mese da (refinement 22/02/2026). */}
          <div className="flex flex-col gap-0.5 min-w-[140px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Mese da
            </label>
            <Select value={filterMonthFrom} onValueChange={setFilterMonthFrom} disabled={filterYear === "all"}>
              <SelectTrigger className="w-[140px] h-9" data-testid="diary-month-from-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i mesi</SelectItem>
                {MONTHS_IT.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Mese a (refinement 22/02/2026) — opzionale; se vuoto e Mese
              da è selezionato, filtra sul singolo mese. */}
          <div className="flex flex-col gap-0.5 min-w-[140px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Mese a
            </label>
            <Select value={filterMonthTo} onValueChange={setFilterMonthTo} disabled={filterYear === "all" || filterMonthFrom === "all"}>
              <SelectTrigger className="w-[140px] h-9" data-testid="diary-month-to-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">—</SelectItem>
                {MONTHS_IT.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Tipo */}
          <div className="flex flex-col gap-0.5 min-w-[130px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Tipo
            </label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[130px] h-9" data-testid="diary-type-filter"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                {CONTACT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Cliente */}
          <div className="flex flex-col gap-0.5 min-w-[160px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Cliente
            </label>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-[160px] h-9" data-testid="diary-client-filter"><SelectValue placeholder="Cliente" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i clienti</SelectItem>
                {contactClients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Operatore */}
          <div className="flex flex-col gap-0.5 min-w-[150px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Operatore
            </label>
            <Select value={operatorFilter} onValueChange={setOperatorFilter}>
              <SelectTrigger className="w-[150px] h-9" data-testid="diary-operator-filter"><SelectValue placeholder="Operatore" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {operators.map(o => <SelectItem key={o.id} value={o.id}>{o.fullName || o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {hasActiveFilters && <Button variant="ghost" size="sm" onClick={resetFilters} className="h-9 text-gray-500"><Filter className="h-3.5 w-3.5 mr-1" /> Reset</Button>}
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-[90px] cursor-pointer select-none" onClick={() => handleSort("data")} data-testid="sort-data">
                <span className="inline-flex items-center gap-1">Data <SortIcon field="data" /></span>
              </TableHead>
              <TableHead className="w-[50px] cursor-pointer select-none" onClick={() => handleSort("ora")} data-testid="sort-ora">
                <span className="inline-flex items-center gap-1">Ora <SortIcon field="ora" /></span>
              </TableHead>
              <TableHead className="w-[100px] cursor-pointer select-none" onClick={() => handleSort("tipo")} data-testid="sort-tipo">
                <span className="inline-flex items-center gap-1">Tipo <SortIcon field="tipo" /></span>
              </TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => handleSort("cliente")} data-testid="sort-cliente">
                <span className="inline-flex items-center gap-1">Cliente <SortIcon field="cliente" /></span>
              </TableHead>
              <TableHead className="w-[100px] cursor-pointer select-none" onClick={() => handleSort("pratica")} data-testid="sort-pratica">
                <span className="inline-flex items-center gap-1">Pratica <SortIcon field="pratica" /></span>
              </TableHead>
              <TableHead>Descrizione</TableHead>
              <TableHead className="w-[110px] cursor-pointer select-none" onClick={() => handleSort("operatore")} data-testid="sort-operatore">
                <span className="inline-flex items-center gap-1">Operatore <SortIcon field="operatore" /></span>
              </TableHead>
              <TableHead className="w-[110px] cursor-pointer select-none" onClick={() => handleSort("destinatario")} data-testid="sort-destinatario">
                <span className="inline-flex items-center gap-1">Destinatario <SortIcon field="destinatario" /></span>
              </TableHead>
              <TableHead className="w-[110px]">Stato</TableHead>
              <TableHead className="w-[90px] text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Empty-state row SEMPRE renderizzata con `hidden` toggled:
                evita la transizione strutturale figli del <TableBody>
                tra "1 row empty" e "N rows" che causava
                `removeChild` durante i cambi di filtro. */}
            <TableRow key="__empty__" hidden={filteredContacts.length !== 0}>
              <TableCell colSpan={10} className="text-center py-12 text-gray-400">
                {hasActiveFilters ? "Nessuna nota trovata" : "Nessuna nota. Clicca \"Nuova Nota\" per iniziare."}
              </TableCell>
            </TableRow>
            {pagedContacts.map(contact => {
              const badge = getContactBadge(contact.contactType);
              const IconComp = badge.icon;
              const isClosed = !!contact.completedAt;
              // Distinguish auto-archived informational notes from
              // manually completed actionable ones. We check the
              // explicit `requiresAction` flag first (new entries
              // since 18/02/2026), then fall back to history scan
              // for retro-compat with the few entries that were
              // mass-created during a session before the field
              // was widely set.
              const isInformational = isClosed && (
                contact.requiresAction === false
                || (Array.isArray(contact.history)
                    && contact.history.some(h => h?.action === "auto_archived"))
              );
              return (
                <TableRow
                  key={contact.id}
                  className={isClosed ? "hover:bg-emerald-50/30 text-slate-600" : "hover:bg-blue-50/30"}
                  data-testid={`diary-row-${contact.id}`}
                >
                  <TableCell className="text-sm font-medium">{formatDate(contact.contactDate)}</TableCell>
                  <TableCell className="text-sm text-gray-500">{contact.contactTime || "-"}</TableCell>
                  <TableCell><Badge variant="secondary" className={`${badge.color} text-xs gap-1`}><IconComp className="h-3 w-3" />{contact.contactType}</Badge></TableCell>
                  <TableCell className="font-medium text-sm">{contact.clientName || "-"}</TableCell>
                  <TableCell>{contact.caseNumber ? <Badge variant="outline" className="text-xs font-mono">{contact.caseNumber}</Badge> : <span className="text-gray-400 text-xs">-</span>}</TableCell>
                  <TableCell className="text-sm text-gray-700 max-w-[250px] truncate">{contact.content}</TableCell>
                  <TableCell className="text-sm text-gray-600">{contact.operatorName || "-"}</TableCell>
                  <TableCell className="text-sm text-gray-600">{contact.destinatarioName || "-"}</TableCell>
                  <TableCell className="text-sm">
                    {isClosed ? (
                      isInformational ? (
                        <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-700 border-slate-300" data-testid={`diary-info-badge-${contact.id}`}>
                          Informativa
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-emerald-500 text-emerald-700 bg-emerald-50 text-xs" data-testid={`diary-status-closed-${contact.id}`}>
                          Chiusa
                        </Badge>
                      )
                    ) : (
                      <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200 text-xs" data-testid={`diary-status-open-${contact.id}`}>
                        In corso
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {isClosed ? (
                        <Button
                          variant="ghost" size="sm"
                          className="h-7 px-2 gap-1.5 text-slate-600 hover:text-slate-800"
                          onClick={() => handleReopen(contact)}
                          data-testid={`diary-reopen-${contact.id}`}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          <span className="text-xs">Riapri</span>
                        </Button>
                      ) : (
                        <Button
                          variant="ghost" size="sm"
                          className="h-7 px-2 gap-1.5 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                          onClick={() => handleCompleteOpen(contact)}
                          data-testid={`diary-complete-${contact.id}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          <span className="text-xs">Chiudi</span>
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 px-2 gap-1.5" onClick={() => openEdit(contact)} data-testid={`diary-edit-${contact.id}`}>
                        <Edit2 className="h-3.5 w-3.5" />
                        <span className="text-xs">Modifica</span>
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        className="h-7 px-2 gap-1.5 text-slate-600 hover:text-slate-800"
                        onClick={() => handlePrintSingleNote(contact)}
                        data-testid={`diary-print-${contact.id}`}
                      >
                        <Printer className="h-3.5 w-3.5" />
                        <span className="text-xs">Stampa</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {/* Barra di paginazione globale unificata (10/30/50/100 + page picker) */}
        <div className="px-4">{contactsPaginationBar}</div>
      </Card>

      {/* Completion dialog — asks for completion date (default = today).
          Gated behind {completingContact && ...} so Radix Dialog doesn't
          mount its portal on every parent render. With 1900+ table rows
          even an idle <Dialog open={false}> contributed measurable cost
          to the React reconciliation pass that triggered the Edge
          "Page non rispondono" → removeChild crash observed in the wild. */}
      {completingContact && (
      <Dialog open={true} onOpenChange={(o) => !o && setCompletingContact(null)}>
        <DialogContent className="max-w-sm" data-testid="diary-complete-dialog">
          <DialogHeader>
            <DialogTitle>Completa nota</DialogTitle>
            <DialogDescription>
              Scegli la data di completamento. L'azione viene registrata nello storico con il tuo nome.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {completingContact && (
              <div className="text-sm text-slate-600 bg-slate-50 rounded-md px-3 py-2">
                <div className="font-medium text-slate-800">{completingContact.clientName || "—"}</div>
                <div className="text-xs mt-0.5 line-clamp-2">{completingContact.content}</div>
              </div>
            )}
            <div>
              <Label>Data completamento</Label>
              <Input
                type="date"
                value={completionDate}
                onChange={(e) => setCompletionDate(e.target.value)}
                className="h-9"
                data-testid="diary-complete-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompletingContact(null)} data-testid="diary-complete-cancel">Annulla</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleCompleteConfirm}
              data-testid="diary-complete-confirm"
            >
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Conferma chiusura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* Create/Edit Dialog — layout 2-colonne (20/02/2026) per dare
          colpo d'occhio completo senza scroll su nuove note. */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="diary-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica Nota" : "Nuova Nota"}</DialogTitle>
            <DialogDescription className="sr-only">
              {editingId
                ? "Modifica la nota esistente. Le modifiche vengono tracciate nella cronologia."
                : "Compila i campi per registrare una nuova nota nel diario giornaliero."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 py-2">
            {/* ── Riga 1 (sx): Data + Ora ── */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data</Label>
                <Input type="date" value={form.contactDate} onChange={e => setForm(f => ({ ...f, contactDate: e.target.value }))} className="h-9" data-testid="diary-date" />
              </div>
              <div>
                <Label>Ora</Label>
                <Input type="time" value={form.contactTime} onChange={e => setForm(f => ({ ...f, contactTime: e.target.value }))} className="h-9" data-testid="diary-time" />
              </div>
            </div>

            {/* ── Riga 1 (dx): Operatore (locked) ── */}
            <div>
              <Label>Operatore</Label>
              <Input value={form.operatorName} readOnly className="h-9 bg-gray-50 text-gray-500 cursor-not-allowed" data-testid="diary-operator-locked" />
            </div>

            {/* ── Riga 2 (sx): Cliente ── */}
            <div>
              <Label>Cliente *</Label>
              <ClientCombobox
                value={form.clientId}
                onChange={handleClientChange}
                clients={clients}
                onCreateNew={onOpenNewClient ? handleCreateNewClientFromDiary : undefined}
                testId="diary-client-input"
              />
            </div>

            {/* ── Riga 2 (dx): Pratica collegata ── */}
            <div>
              <Label>Pratica collegata</Label>
              <Select value={form.caseId || "none"} onValueChange={handleCaseChange}>
                <SelectTrigger data-testid="diary-case-select"><SelectValue placeholder="Nessuna pratica" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuna pratica</SelectItem>
                  {clientActiveCases.map(c => <SelectItem key={c.id} value={c.id}>{c.caseNumber} - {c.subject}</SelectItem>)}
                </SelectContent>
              </Select>
              {clientActiveCases.length === 0 && form.clientId && (
                <p className="text-xs text-gray-400 mt-1">Nessuna pratica attiva per questo cliente</p>
              )}
            </div>

            {/* ── Riga 3 (sx): Tipo contatto ── */}
            <div>
              <Label>Tipo contatto *</Label>
              <Select value={form.contactType} onValueChange={v => setForm(f => ({ ...f, contactType: v }))}>
                <SelectTrigger data-testid="diary-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONTACT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* ── Riga 3 (dx): Area ── */}
            <div>
              <Label className="flex items-center gap-2">
                Area
                {form.caseId && (
                  <Badge variant="outline" className="text-[10px] font-normal">
                    da pratica
                  </Badge>
                )}
              </Label>
              <Select
                value={form.managementType || "agenzia"}
                onValueChange={v => setForm(f => ({
                  ...f,
                  managementType: v,
                  destinatarioId: "",
                  destinatarioName: "",
                  destinatarioIds: [],
                }))}
                disabled={!!form.caseId}
              >
                <SelectTrigger data-testid="diary-management-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agenzia">Gestione Agenzia</SelectItem>
                  <SelectItem value="studio">Gestione Studio</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* ── Riga 4 (full): Destinatario (chips possono allungarsi) ── */}
            <div className="md:col-span-2">
              <Label>Destinatario</Label>
              <OperatorMultiSelect
                operators={filterOperatorsByManagementType(operators, form.managementType)}
                value={form.destinatarioIds && form.destinatarioIds.length
                  ? form.destinatarioIds
                  : (form.destinatarioId ? [form.destinatarioId] : [])}
                onChange={(list) => {
                  const first = list[0] || "";
                  const firstOp = (operators || []).find(o => o.id === first);
                  setForm(f => ({
                    ...f,
                    destinatarioId: first,
                    destinatarioIds: list,
                    destinatarioName: firstOp?.fullName || firstOp?.name || "",
                  }));
                }}
                placeholder="Seleziona uno o più destinatari…"
                dataTestid="diary-dest-multi"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Solo operatori compatibili con l'area
                {" "}
                <span className="font-medium">
                  {(form.managementType || "agenzia") === "studio" ? "Studio" : "Agenzia"}
                </span>
                . Amministratori e responsabili sono sempre inclusi.
              </p>
            </div>

            {/* ── Riga 5 (full): Descrizione ── */}
            <div className="md:col-span-2">
              <Label>Descrizione *</Label>
              <Textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Descrivi l'attività..." rows={4} data-testid="diary-content" />
            </div>

            {/* ── Riga 6 (full): Richiede azione + Cronologia + Risposte
                (tutto a tutta larghezza per leggibilità). ── */}
            <div className="md:col-span-2 space-y-3">

            {/* Operational simplification (18/02/2026). The default
                workflow is "info-only": save and forget. Tick this
                box only when the note represents work that must be
                tracked / actioned by the recipient. */}
            <label
              className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50/70 px-3 py-2 cursor-pointer hover:bg-slate-100/70 transition-colors"
              data-testid="diary-requires-action-row"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 cursor-pointer accent-amber-600"
                checked={!!form.requiresAction}
                onChange={e => setForm(f => ({ ...f, requiresAction: e.target.checked }))}
                data-testid="diary-requires-action-checkbox"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-800">Richiede azione</div>
                <div className="text-[11px] text-slate-500 leading-snug">
                  Lasciato vuoto, la nota è informativa e viene archiviata subito.
                  Spunta per tenerla nell&apos;elenco attivo e notificare il destinatario.
                </div>
              </div>
            </label>

            {editingId && editingHistory && editingHistory.length > 0 && (
              <div
                className="mt-2 rounded-md border border-slate-200 bg-slate-50/60 p-3"
                data-testid="diary-history-section"
              >
                <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <History className="h-3.5 w-3.5" />
                  Cronologia
                </div>
                <ol
                  className="space-y-1.5 max-h-40 overflow-y-auto pr-1"
                  data-testid="diary-history-list"
                >
                  {editingHistory.map((entry, idx) => (
                    <li
                      key={`${entry?.timestamp || idx}-${entry?.action || "x"}-${idx}`}
                      className="text-xs text-slate-700 flex items-start gap-2"
                      data-testid={`diary-history-entry-${idx}`}
                    >
                      <span
                        className={
                          "mt-1 inline-block h-1.5 w-1.5 rounded-full flex-shrink-0 " +
                          (entry?.action === "created"
                            ? "bg-blue-500"
                            : entry?.action === "marked_done" || entry?.action === "diary_completed"
                              ? "bg-emerald-500"
                              : entry?.action === "changed_recipient"
                                ? "bg-violet-500"
                                : entry?.action === "reply"
                                  ? "bg-sky-500"
                                  : "bg-amber-500")
                        }
                      />
                      <span>
                        {buildHistorySentence(entry)}
                        {entry?.action === "reply" && entry?.details && (
                          <em className="not-italic block text-slate-600 mt-0.5 pl-2 border-l-2 border-sky-200">
                            “{entry.details}”
                          </em>
                        )}
                      </span>
                    </li>
                  ))}
                </ol>
                {editingMeta?.operatorName && (
                  <p
                    className="text-[11px] text-slate-400 mt-2 italic"
                    data-testid="diary-history-author"
                  >
                    Autore originale: {editingMeta.operatorName}
                    {editingMeta?.createdAt ? ` · ${formatHistoryWhen(editingMeta.createdAt)}` : ""}
                  </p>
                )}
              </div>
            )}

            {/* S2 Connected-Modules — Cross-module actions su Modifica
                Nota. Bottone "Crea Scadenza" + badge delle scadenze già
                linkate a questa nota (clickable). Visibile solo in
                modalità modifica perché serve un editingId valido. */}
            {editingId && (
              <div
                className="mt-2 rounded-md border border-violet-200 bg-violet-50/40 p-3"
                data-testid="diary-cross-actions"
              >
                <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold uppercase tracking-wide text-violet-700">
                  <CalendarDays className="h-3.5 w-3.5" />
                  Azioni operative
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 border-violet-300 text-violet-700 hover:bg-violet-100"
                    onClick={openDeadlineDialog}
                    data-testid="diary-create-deadline-btn"
                  >
                    <CalendarDays className="h-3.5 w-3.5 mr-1" /> Crea Scadenza
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                    onClick={openPracticeDialog}
                    data-testid="diary-create-practice-btn"
                  >
                    <ClipboardList className="h-3.5 w-3.5 mr-1" /> Nuova Pratica
                  </Button>
                  {linkedDeadlines.length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="diary-linked-deadlines">
                      {linkedDeadlines.map((d) => (
                        <span
                          key={d.id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-violet-100 text-violet-800 border border-violet-200"
                          title={`Tipo: ${d.deadlineType || "—"}\nData: ${d.deadlineDate}\nStato: ${d.status || "attiva"}`}
                          data-testid={`diary-deadline-badge-${d.id}`}
                        >
                          <CalendarDays className="h-3 w-3" />
                          Scadenza · {d.deadlineDate}
                        </span>
                      ))}
                    </div>
                  )}
                  {linkedPractices.length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="diary-linked-practices">
                      {linkedPractices.map((p) => (
                        <span
                          key={p.id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-emerald-100 text-emerald-800 border border-emerald-200"
                          title={`Lavoro · ${getAreaLabel(p.operationalArea)} · ${p.expectedDueDate}\nStato: ${p.status}`}
                          data-testid={`diary-practice-badge-${p.id}`}
                        >
                          <ClipboardList className="h-3 w-3" />
                          Convertita in pratica
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* S1 Connected-Modules — Risposte operative interne.
                Visibile solo in modalità modifica. Le reply sono persistite
                come `history[] action="reply"` (stessa collection, stesso
                audit trail; vedi backend `POST /api/contacts/{id}/reply`).
                Notifiche fan-out lato server a author/destinatario/previous
                repliers. */}
            {editingId && (
              <div
                className="mt-2 rounded-md border border-sky-200 bg-sky-50/40 p-3"
                data-testid="diary-reply-section"
              >
                <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold uppercase tracking-wide text-sky-700">
                  <MessageSquare className="h-3.5 w-3.5" />
                  Risposte operative
                </div>
                <Textarea
                  rows={2}
                  value={replyDraft}
                  onChange={(e) => setReplyDraft(e.target.value)}
                  placeholder="Scrivi una risposta operativa interna…"
                  className="bg-white text-sm"
                  data-testid="diary-reply-input"
                  onKeyDown={(e) => {
                    // Ctrl/Cmd + Enter = invia, in linea col pattern
                    // di Lavoro Quotidiano (note-keyboard shortcut).
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      handleSendReply();
                    }
                  }}
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[11px] text-slate-500">
                    Le risposte vengono notificate ad autore, destinatario e partecipanti al thread.
                  </span>
                  <Button
                    size="sm"
                    onClick={handleSendReply}
                    disabled={!replyDraft.trim() || replySending}
                    data-testid="diary-reply-send-btn"
                    className="h-8 bg-sky-600 hover:bg-sky-700 text-white"
                  >
                    {replySending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquare className="h-3.5 w-3.5 mr-1" />}
                    Invia risposta
                  </Button>
                </div>
              </div>
            )}
            </div>  {/* /md:col-span-2 wrapper */}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            {editingId && (() => {
              // "Stampa scheda" disponibile solo in modalità modifica
              // (per "Nuova Nota" non c'è ancora nulla di salvato).
              // Ricostruiamo la nota dalla lista (per i campi
              // server-side come completedAt/operatorName/history)
              // MERGED con il form corrente, così la stampa riflette
              // anche le modifiche non ancora salvate.
              const existing = contacts.find((c) => c.id === editingId) || {};
              const merged = { ...existing, ...form, id: editingId };
              return (
                <Button
                  variant="outline"
                  onClick={() => handlePrintSingleNote(merged)}
                  data-testid="diary-form-print"
                  className="text-slate-700"
                >
                  <Printer className="h-4 w-4 mr-1.5" /> Stampa scheda
                </Button>
              );
            })()}
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={isSaving || !form.clientId || !form.content.trim()} data-testid="diary-save-btn" className="bg-blue-600 hover:bg-blue-700">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingId ? "Salva Modifiche" : "Registra"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* S2 Connected-Modules — Inline "Crea Scadenza" dialog.
          Form minimale (data + tipo + reminder + note). Backend
          `createCustomDeadline` riceve `sourceModule="diario"` +
          `sourceId=editingId` per la linkage. */}
      <Dialog open={showDeadlineDialog} onOpenChange={setShowDeadlineDialog}>
        <DialogContent className="max-w-md" data-testid="diary-deadline-dialog">
          <DialogHeader>
            <DialogTitle>Crea Scadenza</DialogTitle>
            <DialogDescription className="sr-only">
              Crea una scadenza collegata a questa nota. Il cliente e l'area
              vengono ereditati automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-[11px] text-slate-500 bg-slate-50 rounded p-2 border border-slate-200">
              <strong>Cliente:</strong> {form.clientName || "—"} ·
              <strong className="ml-1">Gestione:</strong> {(form.managementType || "agenzia") === "studio" ? "Studio" : "Agenzia"}
            </div>
            <div>
              <Label className="text-xs font-medium">Tipo scadenza *</Label>
              <Input
                value={deadlineForm.deadlineType}
                onChange={(e) => setDeadlineForm((f) => ({ ...f, deadlineType: e.target.value }))}
                placeholder="Es. Verifica documenti, Sollecito, Appuntamento…"
                data-testid="diary-deadline-type"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Data scadenza *</Label>
                <Input
                  type="date"
                  value={deadlineForm.deadlineDate}
                  onChange={(e) => setDeadlineForm((f) => ({ ...f, deadlineDate: e.target.value }))}
                  data-testid="diary-deadline-date"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">Promemoria</Label>
                <Select
                  value={deadlineForm.reminderType}
                  onValueChange={(v) => setDeadlineForm((f) => ({ ...f, reminderType: v }))}
                >
                  <SelectTrigger className="h-9 text-sm" data-testid="diary-deadline-reminder">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">Una volta</SelectItem>
                    <SelectItem value="daily">Giornaliero</SelectItem>
                    <SelectItem value="weekly">Settimanale</SelectItem>
                    <SelectItem value="monthly">Mensile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium">Note</Label>
              <Textarea
                rows={2}
                value={deadlineForm.notes}
                onChange={(e) => setDeadlineForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Pre-compilate dalla nota; modificabili."
                data-testid="diary-deadline-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeadlineDialog(false)}>
              Annulla
            </Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700"
              onClick={handleCreateDeadline}
              disabled={deadlineSaving || !deadlineForm.deadlineDate || !deadlineForm.deadlineType.trim()}
              data-testid="diary-deadline-save-btn"
            >
              {deadlineSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CalendarDays className="h-4 w-4 mr-1.5" />}
              Crea Scadenza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* S3 Connected-Modules — Inline "Nuova Pratica" dialog.
          Form minimale (area + data + descrizione). Backend
          `createDailyPractice` riceve `sourceModule="diario"` +
          `sourceContactId=editingId`. La nota NON viene chiusa
          automaticamente (lasciata aperta per consentire follow-up). */}
      <Dialog open={showPracticeDialog} onOpenChange={setShowPracticeDialog}>
        <DialogContent className="max-w-md" data-testid="diary-practice-dialog">
          <DialogHeader>
            <DialogTitle>Nuova Pratica dalla nota</DialogTitle>
            <DialogDescription className="sr-only">
              Converti la nota in un lavoro operativo. Cliente e gestione
              vengono ereditati automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-[11px] text-slate-500 bg-slate-50 rounded p-2 border border-slate-200">
              <strong>Cliente:</strong> {form.clientName || "—"} ·
              <strong className="ml-1">Gestione:</strong> {(form.managementType || "agenzia") === "studio" ? "Studio" : "Agenzia"}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Area operativa *</Label>
                <Select
                  value={practiceForm.operationalArea}
                  onValueChange={(v) => setPracticeForm((f) => ({ ...f, operationalArea: v }))}
                >
                  <SelectTrigger className="h-9 text-sm" data-testid="diary-practice-area">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AREA_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium">Data lavoro</Label>
                <Input
                  type="date"
                  value={practiceForm.expectedDueDate}
                  onChange={(e) => setPracticeForm((f) => ({ ...f, expectedDueDate: e.target.value }))}
                  data-testid="diary-practice-date"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium">Descrizione *</Label>
              <Textarea
                rows={3}
                value={practiceForm.description}
                onChange={(e) => setPracticeForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Pre-compilata dal contenuto della nota; modificabile."
                data-testid="diary-practice-description"
              />
            </div>
            <p className="text-[11px] text-slate-500 italic">
              La nota originale resterà aperta dopo la conversione.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPracticeDialog(false)}>
              Annulla
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleCreatePractice}
              disabled={practiceSaving || !practiceForm.description.trim()}
              data-testid="diary-practice-save-btn"
            >
              {practiceSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ClipboardList className="h-4 w-4 mr-1.5" />}
              Crea Pratica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
